import { tableClearMode } from '@didto/domain';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useToast } from '@/app/toast';
import { runCommand } from '@/lib/api';
import { toAppError } from '@/lib/errors';
import { hintOf } from '@/lib/orders';
import { fetchOpenSessions, sessionKeys } from '@/lib/tableSessions';

/** Open table sessions of a location: the occupancy the POS, the Expo and the Tables page show. */
export function useOpenSessions(locationId: string) {
  return useQuery({
    queryKey: sessionKeys.open(locationId),
    enabled: !!locationId,
    queryFn: () => fetchOpenSessions(locationId),
    refetchInterval: 30_000,
  });
}

export interface ClearTarget {
  id: string;
  name: string;
}
export interface OpenOrderSummary {
  order_id: string;
  order_number: string;
  payment_status: string;
  fulfilment_status: string;
}
export interface ForceClearRequest {
  table: ClearTarget;
  openOrders: OpenOrderSummary[];
}

const CLEAR_INVALIDATE = [['table-sessions'], ['orders'], ['expo']];

/**
 * CLEAR TABLE (ADR-021, ADR-022). A table with no open order clears in one tap (SERVER, CASHIER,
 * OWNER). When orders at the table are still open the server refuses the normal clear and lists
 * them: an employee holding table.force_clear (CASHIER, OWNER) gets the force clear sheet with a
 * required reason; anyone else is told to ask the cashier. No manager and no PIN are involved.
 * Pass the location for a browser session (a device acts at its own location).
 */
export function useTableClear(locationId: string | null, canForce: boolean) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [force, setForce] = useState<ForceClearRequest | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(
    () => Promise.all(CLEAR_INVALIDATE.map((k) => qc.invalidateQueries({ queryKey: k }))),
    [qc],
  );

  const clear = useCallback(
    async (table: ClearTarget): Promise<boolean> => {
      setBusy(table.id);
      try {
        await runCommand('table.clear', { table_id: table.id }, locationId ? { locationId } : {});
        await refresh();
        toast(`${table.name} cleared`, 'success');
        return true;
      } catch (e) {
        const err = toAppError(e);
        const hint = hintOf(err);
        if (err.code === 'conflict' && hint.requires === 'table.force_clear') {
          const openOrders = Array.isArray(hint.open_orders)
            ? (hint.open_orders as OpenOrderSummary[])
            : [];
          const n = Math.max(openOrders.length, 1);
          if (tableClearMode(n, canForce) === 'force') setForce({ table, openOrders });
          else
            toast(
              `${n} open order${n === 1 ? '' : 's'} at ${table.name}. Ask the cashier to settle ${n === 1 ? 'it' : 'them'} or force-clear the table.`,
              'danger',
            );
          return false;
        }
        toast(err.message, 'danger');
        return false;
      } finally {
        setBusy(null);
      }
    },
    [locationId, canForce, refresh, toast],
  );

  const forceClear = useCallback(
    async (reason: string): Promise<boolean> => {
      if (!force) return false;
      setBusy(force.table.id);
      try {
        await runCommand(
          'table.force_clear',
          { table_id: force.table.id, reason },
          locationId ? { locationId } : {},
        );
        setForce(null);
        await refresh();
        toast(`${force.table.name} force-cleared`, 'success');
        return true;
      } catch (e) {
        toast(toAppError(e).message, 'danger');
        return false;
      } finally {
        setBusy(null);
      }
    },
    [force, locationId, refresh, toast],
  );

  const cancel = useCallback(() => setForce(null), []);
  return { clear, force, forceClear, cancel, busy };
}
